import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/pages/admin/AdminShell.jsx";
import { Plus, Send, X, Star, Mail, Phone, ExternalLink, Trash2, DollarSign } from "lucide-react";

const EMPTY_FL = {
  name: "", email: "", phone: "", city: "", country: "IN",
  roles: [], skills: [], rate_per_hour: null, rate_per_project: null, currency: "INR",
  portfolio_url: "", linkedin_url: "", source: "", status: "active", tags: [], notes: "",
};

export default function Freelancers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [compose, setCompose] = useState(null);
  const [drawer, setDrawer] = useState(null);

  const load = async () => {
    const r = await api.get("/freelancers");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const allRoles = useMemo(() => Array.from(new Set(rows.flatMap((f) => f.roles || []))).sort(), [rows]);

  const filtered = useMemo(() => rows.filter((f) => {
    if (statusFilter && f.status !== statusFilter) return false;
    if (roleFilter && !(f.roles || []).includes(roleFilter)) return false;
    if (q) {
      const hay = `${f.name} ${f.email || ""} ${f.city || ""} ${(f.skills || []).join(" ")} ${(f.roles || []).join(" ")}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [rows, q, roleFilter, statusFilter]);

  const togglePick = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    if (!editing.name) return toast.warning("Name required");
    const payload = { ...editing };
    delete payload.freelancer_id; delete payload.engagements; delete payload.payments; delete payload.created_at; delete payload.archived; delete payload.rating_avg;
    if (editing.freelancer_id) {
      await api.put(`/freelancers/${editing.freelancer_id}`, payload);
    } else {
      await api.post("/freelancers", payload);
    }
    toast.success("Saved"); setEditing(null); load();
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this freelancer?")) return;
    await api.delete(`/freelancers/${id}`);
    toast.success("Archived"); load();
  };

  const send = async () => {
    if (!compose.body) return toast.warning("Body required");
    const r = await api.post("/freelancers/send", {
      freelancer_ids: compose.use_role_filter ? [] : [...picked],
      role_filter: compose.use_role_filter ? compose.role_filter : null,
      channel: compose.channel, subject: compose.subject, body: compose.body,
      wa_body: compose.wa_body, wati_template_name: compose.wati_template_name,
    });
    toast.success(`Sent to ${r.data.sent} · ${r.data.failed} failed · ${r.data.recipients} total`);
    setCompose(null); setPicked(new Set());
  };

  return (
    <div data-testid="freelancers-page">
      <PageHeader
        eyebrow="PEOPLE · FREELANCERS"
        title="Freelancers"
        subtitle={`${filtered.length} of ${rows.length} · ${rows.filter((f) => f.status === "active").length} active`}
        stats={[
          { label: "Active", value: rows.filter((f) => f.status === "active").length, accent: "pass" },
          { label: "Roles", value: allRoles.length },
          { label: "Avg rating", value: (rows.filter((f) => f.rating_avg).reduce((a, f) => a + f.rating_avg, 0) / Math.max(1, rows.filter((f) => f.rating_avg).length)).toFixed(1) || "—" },
        ]}
        action={
          <div className="flex gap-2">
            {picked.size > 0 && (
              <button onClick={() => setCompose({ channel: "both", body: "Hi {{first_name}}, …", subject: "Opportunity at Thick & Thin" })} className="tt-btn-primary text-sm flex items-center gap-1" data-testid="fl-send-btn"><Send size={13} /> Send ({picked.size})</button>
            )}
            <button onClick={() => setEditing({ ...EMPTY_FL })} className="tt-btn-primary flex items-center gap-2" data-testid="fl-new-btn"><Plus size={14} /> New freelancer</button>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input className="tt-input !w-72" placeholder="Search name / email / skills / city" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fl-search" />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="tt-input !w-auto text-xs" data-testid="fl-role-filter">
          <option value="">All roles</option>
          {allRoles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="tt-input !w-auto text-xs">
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="paused">paused</option>
          <option value="inactive">inactive</option>
        </select>
        <div className="flex-1" />
        {picked.size > 0 && <button onClick={() => setCompose({ channel: "both", body: "Hi {{first_name}},", subject: "Project enquiry", use_role_filter: true, role_filter: roleFilter })} className="tt-btn-secondary text-xs">Send to all in filter</button>}
      </div>

      <div className="tt-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F]">
            <tr className="text-[#71717A] text-[10px] font-mono tracking-wider uppercase">
              <th className="px-4 py-2 w-8"><input type="checkbox" className="accent-[#5B9BFF]" checked={picked.size > 0 && picked.size === filtered.length} onChange={() => setPicked(picked.size === filtered.length ? new Set() : new Set(filtered.map((f) => f.freelancer_id)))} /></th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Roles</th>
              <th className="text-left px-4 py-2">Rate</th>
              <th className="text-left px-4 py-2">City</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Rating</th>
              <th className="text-left px-4 py-2">Engagements</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => (
              <tr key={f.freelancer_id} className="border-t border-[#1E1E1E] hover:bg-[#121212]">
                <td className="px-4 py-3"><input type="checkbox" className="accent-[#5B9BFF]" checked={picked.has(f.freelancer_id)} onChange={() => togglePick(f.freelancer_id)} onClick={(e) => e.stopPropagation()} /></td>
                <td className="px-4 py-3 cursor-pointer" onClick={() => setDrawer(f)}>
                  <div className="font-medium">{f.name}</div>
                  <div className="text-xs font-mono text-[#71717A]">{f.email || f.phone || "—"}</div>
                </td>
                <td className="px-4 py-3"><div className="flex gap-1 flex-wrap">{(f.roles || []).map((r) => <span key={r} className="tt-badge-neutral text-[10px]">{r}</span>)}</div></td>
                <td className="px-4 py-3 font-mono text-xs">{f.rate_per_hour ? `${f.currency} ${f.rate_per_hour}/h` : f.rate_per_project ? `${f.currency} ${f.rate_per_project}/proj` : "—"}</td>
                <td className="px-4 py-3">{f.city || "—"}</td>
                <td className="px-4 py-3"><span className={f.status === "active" ? "tt-badge-pass" : f.status === "paused" ? "tt-badge-warn" : "tt-badge-neutral"}>{f.status}</span></td>
                <td className="px-4 py-3 font-mono text-xs">{f.rating_avg ? `★ ${f.rating_avg}` : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#A1A1AA]">{(f.engagements || []).length}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#71717A]">No freelancers yet. Click <span className="text-[#F4F4F5]">New freelancer</span>.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.9)] flex items-start justify-center z-40 p-6 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl tt-card my-6" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-4 border-b border-[#1E1E1E] flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editing.freelancer_id ? "Edit freelancer" : "New freelancer"}</h2>
              <button onClick={() => setEditing(null)} className="tt-btn-ghost text-[#71717A]"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name"><input className="tt-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="fl-name" /></Field>
                <Field label="Email"><input className="tt-input" type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="fl-email" /></Field>
                <Field label="Phone"><input className="tt-input" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} data-testid="fl-phone" /></Field>
                <Field label="City"><input className="tt-input" value={editing.city || ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} /></Field>
              </div>
              <Field label="Roles (comma-separated)" hint="editor, designer, copywriter, motion, dev …">
                <input className="tt-input" value={(editing.roles || []).join(", ")} onChange={(e) => setEditing({ ...editing, roles: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid="fl-roles" />
              </Field>
              <Field label="Skills (comma-separated)">
                <input className="tt-input" value={(editing.skills || []).join(", ")} onChange={(e) => setEditing({ ...editing, skills: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Rate / hour"><input type="number" className="tt-input font-mono" value={editing.rate_per_hour || ""} onChange={(e) => setEditing({ ...editing, rate_per_hour: e.target.value ? parseFloat(e.target.value) : null })} /></Field>
                <Field label="Rate / project"><input type="number" className="tt-input font-mono" value={editing.rate_per_project || ""} onChange={(e) => setEditing({ ...editing, rate_per_project: e.target.value ? parseFloat(e.target.value) : null })} /></Field>
                <Field label="Currency"><input className="tt-input font-mono" value={editing.currency} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Portfolio URL"><input className="tt-input text-sm" value={editing.portfolio_url || ""} onChange={(e) => setEditing({ ...editing, portfolio_url: e.target.value })} /></Field>
                <Field label="LinkedIn"><input className="tt-input text-sm" value={editing.linkedin_url || ""} onChange={(e) => setEditing({ ...editing, linkedin_url: e.target.value })} /></Field>
              </div>
              <Field label="Status"><select className="tt-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}><option value="active">active</option><option value="paused">paused</option><option value="inactive">inactive</option></select></Field>
              <Field label="Notes"><textarea rows={3} className="tt-input text-sm" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field>
            </div>
            <div className="px-6 py-4 border-t border-[#1E1E1E] flex justify-between">
              {editing.freelancer_id && <button onClick={() => archive(editing.freelancer_id)} className="tt-btn-ghost text-xs text-[#EF4444] flex items-center gap-1"><Trash2 size={12} /> Archive</button>}
              <div className="flex-1" />
              <div className="flex gap-2">
                <button onClick={() => setEditing(null)} className="tt-btn-secondary text-sm">Cancel</button>
                <button onClick={save} className="tt-btn-primary text-sm" data-testid="fl-save-btn">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {drawer && <FreelancerDrawer f={drawer} onClose={() => setDrawer(null)} onEdit={() => { setEditing(drawer); setDrawer(null); }} onReload={async () => { const r = await api.get(`/freelancers/${drawer.freelancer_id}`); setDrawer(r.data); load(); }} />}

      {compose && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setCompose(null)}>
          <div className="w-full max-w-xl tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Message freelancers</h2>
            <select value={compose.channel} onChange={(e) => setCompose({ ...compose, channel: e.target.value })} className="tt-input mb-3">
              <option value="both">Both · Email + WhatsApp</option>
              <option value="email">Email (Gmail)</option>
              <option value="whatsapp">WhatsApp (Wati)</option>
            </select>
            {(compose.channel === "email" || compose.channel === "both") && <input className="tt-input mb-3" placeholder="Subject" value={compose.subject || ""} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} />}
            <textarea rows={5} className="tt-input mb-3 text-sm" placeholder="Body · supports {{first_name}}" value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} data-testid="fl-compose-body" />
            {compose.channel === "both" && <textarea rows={3} className="tt-input mb-3 text-sm" placeholder="WhatsApp body (optional)" value={compose.wa_body || ""} onChange={(e) => setCompose({ ...compose, wa_body: e.target.value })} />}
            {(compose.channel === "whatsapp" || compose.channel === "both") && <input className="tt-input mb-3 font-mono text-xs" placeholder="Wati template name (optional)" value={compose.wati_template_name || ""} onChange={(e) => setCompose({ ...compose, wati_template_name: e.target.value })} />}
            <div className="text-sm text-[#A1A1AA] mb-4">To: <span className="text-[#F4F4F5]">{compose.use_role_filter ? `all active ${compose.role_filter || "freelancers"}` : `${picked.size} selected`}</span></div>
            <div className="flex justify-end gap-2"><button onClick={() => setCompose(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={send} className="tt-btn-primary text-sm" data-testid="fl-compose-send">Send now</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return <div><div className="tt-label mb-1.5">{label}</div>{children}{hint && <div className="text-xs text-[#71717A] mt-1">{hint}</div>}</div>;
}

function FreelancerDrawer({ f, onClose, onEdit, onReload }) {
  const [newEng, setNewEng] = useState(null);
  const [newPay, setNewPay] = useState(null);
  const totalAgreed = (f.engagements || []).reduce((a, e) => a + (e.agreed_amount || 0), 0);
  const totalPaid = (f.engagements || []).reduce((a, e) => a + (e.paid_amount || 0), 0);

  const saveEng = async () => {
    await api.post(`/freelancers/${f.freelancer_id}/engagements`, newEng);
    toast.success("Engagement logged"); setNewEng(null); onReload();
  };
  const savePay = async () => {
    await api.post(`/freelancers/${f.freelancer_id}/payments`, newPay);
    toast.success("Payment logged"); setNewPay(null); onReload();
  };
  const rateEng = async (eid, rating, review) => {
    await api.patch(`/freelancers/${f.freelancer_id}/engagements/${eid}`, { rating, review, project: "", started_at: "" });
    toast.success("Rating saved"); onReload();
  };

  return (
    <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex justify-end z-40" onClick={onClose}>
      <div className="w-full max-w-[640px] h-full bg-[#0A0A0A] border-l border-[#262626] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold">{f.name}</h2>
            <div className="text-xs font-mono text-[#71717A]">{f.email || "—"} · {f.phone || "—"} · {f.city || "—"}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={onEdit} className="tt-btn-secondary text-xs">Edit</button>
            <button onClick={onClose} className="tt-btn-ghost text-[#71717A]"><X size={16} /></button>
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {(f.roles || []).map((r) => <span key={r} className="tt-badge-info">{r}</span>)}
          <span className={f.status === "active" ? "tt-badge-pass" : "tt-badge-neutral"}>{f.status}</span>
          {f.rating_avg && <span className="tt-badge-neutral">★ {f.rating_avg}</span>}
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5 text-xs">
          <div className="tt-card p-3"><div className="tt-label mb-1">Agreed total</div><div className="font-mono text-lg">{f.currency} {totalAgreed.toFixed(0)}</div></div>
          <div className="tt-card p-3"><div className="tt-label mb-1">Paid</div><div className="font-mono text-lg text-[#22C55E]">{f.currency} {totalPaid.toFixed(0)}</div></div>
          <div className="tt-card p-3"><div className="tt-label mb-1">Due</div><div className="font-mono text-lg text-[#F5A524]">{f.currency} {(totalAgreed - totalPaid).toFixed(0)}</div></div>
        </div>

        <div className="flex items-center justify-between mb-2">
          <div className="tt-label">Engagements</div>
          <button onClick={() => setNewEng({ started_at: new Date().toISOString().slice(0,10), project: "", agreed_amount: 0, currency: f.currency, status: "active" })} className="tt-btn-secondary text-xs flex items-center gap-1"><Plus size={11} /> Log engagement</button>
        </div>
        <div className="space-y-2 mb-5">
          {(f.engagements || []).map((e) => (
            <div key={e.engagement_id} className="tt-card p-3">
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm">{e.project}</div>
                <span className={e.status === "active" ? "tt-badge-info" : e.status === "completed" ? "tt-badge-pass" : "tt-badge-neutral"}>{e.status}</span>
              </div>
              <div className="text-xs font-mono text-[#71717A] mt-1">{e.started_at}{e.ended_at ? ` → ${e.ended_at}` : ""} · {e.currency} {e.agreed_amount} ({e.paid_amount || 0} paid)</div>
              {e.scope && <div className="text-xs text-[#A1A1AA] mt-1">{e.scope}</div>}
              <div className="flex items-center gap-1 mt-2">
                {[1,2,3,4,5].map((n) => (
                  <button key={n} onClick={() => rateEng(e.engagement_id, n, e.review || "")} className={`h-6 w-6 grid place-items-center rounded ${((e.rating || 0) >= n) ? "text-[#F5A524]" : "text-[#3F3F46] hover:text-[#71717A]"}`}><Star size={12} fill={((e.rating || 0) >= n) ? "currentColor" : "none"} /></button>
                ))}
                {e.rating && <span className="text-xs font-mono text-[#71717A] ml-2">★ {e.rating}</span>}
              </div>
              {e.review && <div className="text-xs text-[#A1A1AA] mt-1 italic">"{e.review}"</div>}
            </div>
          ))}
          {(f.engagements || []).length === 0 && <div className="text-sm text-[#71717A] text-center py-5 border border-dashed border-[#262626] rounded-md">No engagements yet.</div>}
        </div>

        <div className="flex items-center justify-between mb-2">
          <div className="tt-label">Payments</div>
          <button onClick={() => setNewPay({ paid_at: new Date().toISOString().slice(0,10), amount: 0, currency: f.currency, engagement_id: (f.engagements || [])[0]?.engagement_id })} className="tt-btn-secondary text-xs flex items-center gap-1"><DollarSign size={11} /> Log payment</button>
        </div>
        <div className="space-y-1">
          {(f.payments || []).slice().reverse().map((p) => (
            <div key={p.payment_id} className="flex items-center justify-between text-sm py-1 border-b border-[#1E1E1E]">
              <div className="font-mono text-xs">{p.paid_at}</div>
              <div className="flex-1 text-xs text-[#A1A1AA] px-3">{p.method || "—"} {p.reference ? `· ${p.reference}` : ""}</div>
              <div className="font-mono text-sm text-[#22C55E]">{p.currency} {p.amount}</div>
            </div>
          ))}
          {(f.payments || []).length === 0 && <div className="text-sm text-[#71717A] text-center py-3">No payments logged.</div>}
        </div>

        {newEng && (
          <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-50 p-4" onClick={() => setNewEng(null)}>
            <div className="w-full max-w-md tt-card p-5" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base font-semibold mb-3">New engagement</h3>
              <input className="tt-input mb-2" placeholder="Project / brief" value={newEng.project} onChange={(e) => setNewEng({ ...newEng, project: e.target.value })} />
              <textarea rows={2} className="tt-input mb-2 text-sm" placeholder="Scope" value={newEng.scope || ""} onChange={(e) => setNewEng({ ...newEng, scope: e.target.value })} />
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input type="date" className="tt-input font-mono text-xs" value={newEng.started_at} onChange={(e) => setNewEng({ ...newEng, started_at: e.target.value })} />
                <input type="date" className="tt-input font-mono text-xs" placeholder="End" value={newEng.ended_at || ""} onChange={(e) => setNewEng({ ...newEng, ended_at: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <input type="number" className="tt-input font-mono text-sm" placeholder="Agreed amount" value={newEng.agreed_amount} onChange={(e) => setNewEng({ ...newEng, agreed_amount: parseFloat(e.target.value || "0") })} />
                <select className="tt-input text-sm" value={newEng.status} onChange={(e) => setNewEng({ ...newEng, status: e.target.value })}><option value="active">active</option><option value="completed">completed</option><option value="cancelled">cancelled</option></select>
              </div>
              <div className="flex justify-end gap-2"><button onClick={() => setNewEng(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={saveEng} className="tt-btn-primary text-sm">Save</button></div>
            </div>
          </div>
        )}

        {newPay && (
          <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-50 p-4" onClick={() => setNewPay(null)}>
            <div className="w-full max-w-md tt-card p-5" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base font-semibold mb-3">Log payment</h3>
              <select className="tt-input mb-2 text-sm" value={newPay.engagement_id || ""} onChange={(e) => setNewPay({ ...newPay, engagement_id: e.target.value })}>
                <option value="">— no specific engagement —</option>
                {(f.engagements || []).map((e) => <option key={e.engagement_id} value={e.engagement_id}>{e.project}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input type="date" className="tt-input font-mono text-xs" value={newPay.paid_at} onChange={(e) => setNewPay({ ...newPay, paid_at: e.target.value })} />
                <input type="number" className="tt-input font-mono text-sm" placeholder="Amount" value={newPay.amount} onChange={(e) => setNewPay({ ...newPay, amount: parseFloat(e.target.value || "0") })} />
              </div>
              <input className="tt-input mb-2 text-sm" placeholder="Method (bank / upi / cash)" value={newPay.method || ""} onChange={(e) => setNewPay({ ...newPay, method: e.target.value })} />
              <input className="tt-input mb-3 text-sm" placeholder="Reference (UTR / invoice no.)" value={newPay.reference || ""} onChange={(e) => setNewPay({ ...newPay, reference: e.target.value })} />
              <div className="flex justify-end gap-2"><button onClick={() => setNewPay(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={savePay} className="tt-btn-primary text-sm">Log</button></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
